import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import FullBlockingModal from '../common/FullBlockingModal';
import { Notification } from '../../stores/notificationStore';
import { useNavigate } from 'react-router-dom';
import ProfileMessageCard from '../common/ProfileMessageCard';
import { useProfileStore } from '../../stores/profileStore';
import GenericInformationModal from '../common/GenericInformationModal';
import {
  getNotificationMessage,
  getActionButton,
  NotificationMessageModalProps,
} from '../../utils/notificationsUtils.tsx';
import pigeonsLogoUrl from '../../assets/images/pigeons_symbole.svg?url';

const NotificationMessageModal = ({
  title,
  subTitle,
  message,
  primaryButton,
  onClose,
}: NotificationMessageModalProps) => {
  const { t } = useTranslation('common');
  return (
    <FullBlockingModal innerClassName="md:max-w-[72rem]">
      <button
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl md:top-0"
        onClick={onClose}
        aria-label={t('common:form_actions.close')}
      >
        &times;
      </button>
      <h2 className="text-2xl font-bold text-secondary">{title}</h2>
      {subTitle && <div className="text-secondary mt-4">{subTitle}</div>}
      <p className="text-secondary text-sm mt-4">{message}</p>
      <div className="flex flex-col gap-4 mt-8 md:flex-row-reverse md:items-center">
        {primaryButton && (
          <Button
            className="w-full md:w-auto md:px-8"
            variant="transparent-with-border"
            href={primaryButton.href}
            state={primaryButton.state}
            onClick={primaryButton.onClick}
          >
            {primaryButton.label}
          </Button>
        )}
      </div>
    </FullBlockingModal>
  );
};

interface NotificationCardProps {
  notification: Notification;
  outerClassName?: string;
}

const NotificationCard: React.FC<NotificationCardProps> = ({ notification, outerClassName }) => {
  const { t } = useTranslation(['notifications', 'common', 'reports']);
  const hasSourceProfile = Boolean(notification.source_profile);
  const sourceName =
    notification.source_profile?.first_name ||
    (hasSourceProfile ? t('first_name_fallback') : 'Pigeons');
  const avatarUrl = hasSourceProfile
    ? notification.source_profile?.avatar_url || ''
    : pigeonsLogoUrl;

  const navigate = useNavigate();

  const { approveSuggestedCategory, approveSuggestedIndustry } = useProfileStore();

  const [message, setMessage] = useState('');

  const handleApproveServiceCategory = async (category_id: number) => {
    const result = await approveSuggestedCategory(category_id);
    setNotificationMessageModal(null);
    if (result === null) {
      setMessage(t('serviceCategoryApproved'));
    } else {
      setMessage(t('serviceCategoryApprovalError'));
    }
  };

  const handleApproveIndustry = async (industry_id: number) => {
    const result = await approveSuggestedIndustry(industry_id);
    setNotificationMessageModal(null);
    if (result === null) {
      setMessage(t('industryApproved'));
    } else {
      setMessage(t('industryApprovalError'));
    }
  };

  const handleReportRedirection = (payload: Record<string, unknown>) => {
    switch (payload.content_type) {
      case 'profile':
        navigate(`/admin/users`, { state: { selected_id: payload.entity_id } });
        break;

      case 'review':
        navigate(`/admin/reviews`, { state: payload.entity_id });
        break;

      case 'job':
        navigate(`/admin/jobs`, { state: { selected_id: payload.entity_id } });
        break;
    }
  };

  const [notificationMessageModal, setNotificationMessageModal] =
    useState<NotificationMessageModalProps | null>(null);

  const onOpenNotificationMessageModal = (modalProps: NotificationMessageModalProps) => {
    setNotificationMessageModal(modalProps);
  };

  const actionButton = getActionButton(
    notification,
    t,
    onOpenNotificationMessageModal,
    handleApproveServiceCategory,
    handleApproveIndustry,
    handleReportRedirection
  );

  // Some action button handlers (e.g. `new_job_posted`) resolve asynchronously
  // while deciding where to navigate. Track that in a per-card loading state so
  // we can show a pending indicator on the button rather than leaving the user
  // without feedback between click and navigation. If the handler unexpectedly
  // rejects instead of resolving, still clear the loading state so the button
  // never gets stuck.
  const [isActionLoading, setIsActionLoading] = useState(false);

  const handleActionButtonClick = () => {
    if (!actionButton.onClick) return;
    const result = actionButton.onClick();
    if (result instanceof Promise) {
      setIsActionLoading(true);
      result.catch(() => undefined).finally(() => setIsActionLoading(false));
    }
  };

  return (
    <>
      <ProfileMessageCard
        profile={{
          id: notification.source_profile?.id,
          firstName: sourceName,
          avatarUrl,
        }}
        title={sourceName}
        message={getNotificationMessage(notification, t)}
        date={notification.created_at}
        showUnreadIndicator={!notification.read_at}
        className={outerClassName}
        actionButton={{
          label: actionButton.label,
          onClick: handleActionButtonClick,
          loading: isActionLoading,
        }}
      />

      {notificationMessageModal && (
        <NotificationMessageModal
          {...notificationMessageModal}
          onClose={notificationMessageModal.onClose || (() => setNotificationMessageModal(null))}
        />
      )}

      {message && (
        <GenericInformationModal
          title={message}
          message=""
          onClose={() => {
            setMessage('');
          }}
          closeButtonLabel={t('close')}
        />
      )}
    </>
  );
};

export default NotificationCard;
export type { NotificationMessageModalProps };
